import React, { useState, useMemo } from 'react';
import {
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';
import '../Pages.css';
import PageHero from '../../components/Common/PageHero';
import ScreenerSearchControls from '../../components/Screener/ScreenerSearchControls';
import ScreenerTable from '../../components/Screener/ScreenerTable';
import MethodologySection from '../../components/Screener/MethodologySection';

interface AlphaData {
  id: number;
  symbol: string;
  company: string;
  alphaScore: number;
  dataIntegrity: number;
  fundamentals: number;
  screening: number;
  deepAnalysis: number;
  aiEnhancement: number;
  portfolioOpt: number;
}

// Sample data - in real app this would come from an API
const MOCK_ALPHA_DATA: AlphaData[] = [
  {
    id: 1,
    symbol: 'NVDA',
    company: 'NVIDIA Corporation',
    alphaScore: 9.3,
    dataIntegrity: 9.1,
    fundamentals: 9.2,
    screening: 9.0,
    deepAnalysis: 9.3,
    aiEnhancement: 9.5,
    portfolioOpt: 9.1
  },
  {
    id: 2,
    symbol: 'AAPL',
    company: 'Apple Inc.',
    alphaScore: 8.6,
    dataIntegrity: 9.2,
    fundamentals: 8.5,
    screening: 8.8,
    deepAnalysis: 8.3,
    aiEnhancement: 8.7,
    portfolioOpt: 8.1
  },
  {
    id: 3,
    symbol: 'GOOGL',
    company: 'Alphabet Inc.',
    alphaScore: 8.8,
    dataIntegrity: 9.0,
    fundamentals: 8.7,
    screening: 8.5,
    deepAnalysis: 8.9,
    aiEnhancement: 9.1,
    portfolioOpt: 8.4
  },
  {
    id: 4,
    symbol: 'MSFT',
    company: 'Microsoft Corporation',
    alphaScore: 8.7,
    dataIntegrity: 9.3,
    fundamentals: 8.9,
    screening: 8.7,
    deepAnalysis: 8.8,
    aiEnhancement: 9.0,
    portfolioOpt: 8.6
  },
  {
    id: 5,
    symbol: 'JPM',
    company: 'JPMorgan Chase & Co.',
    alphaScore: 8.5,
    dataIntegrity: 8.9,
    fundamentals: 8.6,
    screening: 8.4,
    deepAnalysis: 8.7,
    aiEnhancement: 8.3,
    portfolioOpt: 8.5
  },
  {
    id: 6,
    symbol: 'AMZN',
    company: 'Amazon.com Inc.',
    alphaScore: 8.0,
    dataIntegrity: 8.8,
    fundamentals: 7.9,
    screening: 8.2,
    deepAnalysis: 8.0,
    aiEnhancement: 8.4,
    portfolioOpt: 7.8
  },
  {
    id: 7,
    symbol: 'META',
    company: 'Meta Platforms Inc.',
    alphaScore: 8.1,
    dataIntegrity: 8.7,
    fundamentals: 8.0,
    screening: 8.3,
    deepAnalysis: 8.1,
    aiEnhancement: 8.5,
    portfolioOpt: 7.9
  },
  {
    id: 8,
    symbol: 'TSLA',
    company: 'Tesla Inc.',
    alphaScore: 7.5,
    dataIntegrity: 8.5,
    fundamentals: 7.2,
    screening: 7.8,
    deepAnalysis: 7.5,
    aiEnhancement: 8.0,
    portfolioOpt: 7.3
  }
];

const AlphaAnalyzer: React.FC = () => {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'alphaScore', desc: true }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Helper function for score styling
  const getScoreClass = (score: number) => {
    if (score >= 8) return 'score-high';
    if (score >= 6.5) return 'score-medium';
    return 'score-low';
  };

  // Methodology data aligned with Third Locust's 7-step process for Alpha Analysis
  const methodologyItems = [
    {
      title: "Alpha Score",
      description: "Composite score measuring alpha generation potential using our proprietary weighted algorithm across all 6 analysis dimensions"
    },
    {
      title: "Data Integrity",
      description: "Advanced multi-layer validation from 7 top financial data providers with precision assurance protocols"
    },
    {
      title: "Fundamentals",
      description: "Proprietary multifactor scoring across 28 financial ratios and proven valuation methodologies"
    },
    {
      title: "Quantitative Screening",
      description: "Systematic distillation of ~2,500 companies down to elite 8% exhibiting superior fundamentals"
    },
    {
      title: "Deep Analysis",
      description: "Forensic fundamental analysis examining competitive positioning, financial health, and alpha potential"
    },
    {
      title: "AI Enhancement",
      description: "Machine learning algorithms identify hidden market inefficiencies and high-alpha opportunities"
    },
    {
      title: "Portfolio Optimization",
      description: "Strategic weightings optimized based on alpha contribution, risk-adjusted returns, and correlation analysis"
    }
  ];

  const scoreLegend = [
    { range: "8.0+", label: "High Alpha", className: "score-high" },
    { range: "6.5-7.9", label: "Moderate Alpha", className: "score-medium" },
    { range: "<6.5", label: "Low Alpha", className: "score-low" }
  ];

  // Define columns with proper TypeScript types
  const columns: ColumnDef<AlphaData>[] = useMemo(() => [
    {
      accessorKey: 'symbol',
      header: 'Symbol',
      cell: ({ getValue }) => <strong>{String(getValue())}</strong>,
      enableSorting: true,
    },
    {
      accessorKey: 'company',
      header: 'Company',
      cell: ({ getValue }) => String(getValue()),
      enableSorting: true,
    },
    {
      accessorKey: 'alphaScore',
      header: 'Alpha Score',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            <strong>{value.toFixed(1)}</strong>
          </span>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'dataIntegrity',
      header: 'Data Integrity',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            {value.toFixed(1)}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'fundamentals',
      header: 'Fundamentals', 
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            {value.toFixed(1)}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'screening',
      header: 'Screening',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            {value.toFixed(1)}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'deepAnalysis',
      header: 'Deep Analysis',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            {value.toFixed(1)}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'aiEnhancement',
      header: 'AI Enhancement',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            {value.toFixed(1)}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'portfolioOpt',
      header: 'Portfolio Opt.',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            {value.toFixed(1)}
          </span>
        );
      },
      enableSorting: true,
    },
  ], []);

  const table = useReactTable({
    data: MOCK_ALPHA_DATA,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    debugTable: true,
  });

  return (
    <div className="investment-screener-page">
      <PageHero
        title="Alpha Analyzer"
        subtitle="Third Locust's proprietary alpha generation screening system analyzing Russell 2000 & S&P 500 stocks ($200M-$10B market cap) through our systematic 7-step investment process"
        className="investment-screener-hero"
      />

      <ScreenerSearchControls
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        resultsCount={table.getFilteredRowModel().rows.length}
      />

      <ScreenerTable table={table} />

      <MethodologySection
        title="Alpha Analysis Methodology"
        methodologyItems={methodologyItems}
        scoreLegend={scoreLegend}
      />
    </div>
  );
};

export default AlphaAnalyzer;
